import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import React, { useState } from "react";

import type { SelectServerOptions } from "src/components/Form/Form.types.ts";
import {
  SelectV2,
  type SelectV2Props,
} from "src/components/SelectV2/SelectV2.tsx";
import { useSelect } from "src/system/useSelect.tsx";
import { useDebouncedValue } from "@exonn/hooks";
import { useServerSelectFilter } from "src/system/useServerSelectFilter.ts";

export type MultiSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  serverOptions?: SelectServerOptions;
  locale?: string;
} & Omit<SelectV2Props, "isMultiple"> &
  Omit<ControllerProps<TFieldValues, TName>, "render">;

export const MultiSelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: MultiSelectFieldProps<TFieldValues, TName>,
) => {
  const {
    name,
    control,
    rules,
    shouldUnregister,
    showSearch,
    defaultValue,
    disabled,
    locale: lang,
    serverOptions,
    options: optionsProp,
    onChange: onChangeProp,
    helperText,
    label,
    required,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);

  const filter = useServerSelectFilter({
    ...serverOptions,
  });

  const {
    options: resourceOptions,
    query: {
      isLoading: isResourceOptionsLoading,
      refetch: refetchResourceOptions,
    },
  } = useSelect({
    locale: lang,
    resource: serverOptions ? serverOptions.resource : "",
    disablePagination: true,
    useSimplePagination: true,
    search: debouncedSearch,
    searchField: serverOptions?.searchField,
    labelProp: serverOptions?.labelProp,
    valueProp: serverOptions?.valueProp,
    include: serverOptions?.include,
    sort: serverOptions?.sort,
    processOptions: serverOptions?.processOptions,
    filterFn: serverOptions?.filterFn,
    filter: filter,
    queryOptions: {
      enabled: !!serverOptions,
    },
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && serverOptions) {
      refetchResourceOptions();
    }
  };

  return (
    <Controller
      control={control}
      key={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { onChange, value }, formState: { errors } }) => {
        const handleChange = (value: any) => {
          onChangeProp?.(value);
          onChange?.(value);
        };
        const isLoadingData = serverOptions ? isResourceOptionsLoading : false;
        const options = serverOptions ? resourceOptions : optionsProp;
        return (
          <SelectV2
            disabled={disabled}
            isMultiple={true}
            value={value as string[]}
            isLoadingData={isLoadingData}
            isLoadingSelectedOption={isLoadingData}
            onSearchValueChange={setSearch}
            searchValue={search}
            showSearch={showSearch}
            label={label}
            helperText={helperText}
            required={required}
            onOpenChange={handleOpenChange}
            isOpen={isOpen}
            error={errors?.root?.[name]?.message}
            onChange={handleChange}
            options={options}
          />
        );
      }}
    />
  );
};
